import {
  AgsExportConfigurationAdmin,
  AgsExportConfigurationListAdmin,
  ClientUrisAdmin,
  DataIntegratorsAdmin,
  DatabaseConnectionsAdmin,
  EmailAdmin,
  LicenseUsageAdmin,
  LicenseUsageSettingsAdmin,
  LoggingAdmin,
  QestFieldAdmin,
  ReportConfigurationGroupAdmin,
  ReportsAdmin,
  RestartAdmin,
} from 'Components/Admin';
import AdminInjectedPropsProvider from 'Components/Admin/Core/AdminInjectedPropsProvider';
import FixedAdminHeader from 'Components/Admin/Core/Header/FixedAdminHeader';
import EquipmentDriversAdmin from 'Components/Admin/EquipmentDriversAdmin';
import IndividualDriverAdmin from 'Components/Admin/IndividualDriverAdmin';
import NotificationsAdmin from 'Components/Admin/NotificationsAdmin';
import ReportConfigurationAdmin from 'Components/Admin/ReportConfiguration/ReportConfigurationAdmin';
import ReportConfigurationGroupListAdmin from 'Components/Admin/ReportConfiguration/ReportConfigurationGroupListAdmin';
import ReportConfigurationListAdmin from 'Components/Admin/ReportConfiguration/ReportConfigurationListAdmin';
import { Spinner } from 'Components/Core';
import ErrorBoundary from 'Components/Core/ErrorBoundary';
import Message from 'Components/Core/Message';
import { PageSection } from 'Components/Layout';
import { actions } from 'Data/ReduxViewModels/Admin';
import { actions as environmentActions } from 'Data/ReduxViewModels/Environment';
import { isNil } from 'ramda';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Page from '../Core/Page';
import './AdminPage.scss';

const adminPageConfiguration = {
  'license-usage': {
    linkHeading: 'Tracking',
    linkText: 'License Usage',
    component: LicenseUsageAdmin,
    headerName: 'QEST Platform License Usage',
  },
  'ags-export-configuration': {
    linkHeading: 'Settings',
    linkText: 'AGS Export Configuration',
    component: AgsExportConfigurationListAdmin,
    individualComponent: AgsExportConfigurationAdmin,
    headerName: 'AGS Export Configuration',
  },
  'client-uris': {
    linkHeading: 'Settings',
    linkText: 'Client URIs',
    component: ClientUrisAdmin,
    headerName: 'Client URIs',
  },
  'database-connections': {
    linkHeading: 'Settings',
    linkText: 'Database Connections',
    component: DatabaseConnectionsAdmin,
    headerName: 'Database Connections',
  },
  'data-integrators': {
    linkHeading: 'Settings',
    linkText: 'Data Integrators',
    component: DataIntegratorsAdmin,
    headerName: 'Data Integrators',
  },
  email: {
    linkHeading: 'Settings',
    linkText: 'Email',
    component: EmailAdmin,
    headerName: 'Email',
  },
  'equipment-drivers': {
    linkHeading: 'Settings',
    linkText: 'Equipment Drivers',
    component: EquipmentDriversAdmin,
    individualComponent: IndividualDriverAdmin,
    headerName: 'Equipment Drivers',
  },
  'license-usage-settings': {
    linkHeading: 'Settings',
    linkText: 'License Usage',
    component: LicenseUsageSettingsAdmin,
    headerName: 'License Usage Settings',
  },
  logging: {
    linkHeading: 'Settings',
    linkText: 'Logging',
    component: LoggingAdmin,
    headerName: 'Logging',
  },
  notifications: {
    linkHeading: 'Settings',
    linkText: 'Notifications',
    component: NotificationsAdmin,
    environmentDataRequired: true,
    headerName: 'Notifications',
  },
  reports: {
    linkHeading: 'Settings',
    linkText: 'Reports',
    component: ReportsAdmin,
    headerName: 'Reports',
  },
  'report-configuration': {
    linkHeading: 'Settings',
    linkText: 'Report Configuration',
    component: ReportConfigurationListAdmin,
    individualComponent: ReportConfigurationAdmin,
    headerName: 'Report Configuration',
  },
  'report-configuration-group': {
    linkHeading: 'Settings',
    linkText: 'Report Configuration Groups',
    component: ReportConfigurationGroupListAdmin,
    individualComponent: ReportConfigurationGroupAdmin,
    headerName: 'Report Configuration Groups',
  },
  'qest-field': {
    linkHeading: 'System',
    linkText: 'QESTField',
    component: QestFieldAdmin,
    headerName: 'QESTField',
  },
  restart: {
    linkHeading: 'System',
    linkText: 'QESTNet Restart',
    component: RestartAdmin,
    headerName: 'QESTNet Restart',
  },
};

const getLinks = (showNotifications, agsLicensed) => {
  let prevousLinkHeading = '';

  return Object.entries(adminPageConfiguration)
    .filter(([k, _]) => showNotifications || k !== 'notifications')
    .filter(([k, _]) => agsLicensed || k !== 'ags-export-configuration')
    .map(([k, v]) => {
      let heading = prevousLinkHeading === v.linkHeading ? undefined : v.linkHeading;
      prevousLinkHeading = v.linkHeading;

      return {
        uri: `/admin/${k}`,
        text: v.linkText,
        heading: heading,
      };
    });
};

const AdminPage = () => {
  const params = useParams();
  const component = params.component;
  /**
   * Identifies the individual item to display for admin pages with an individualComponent defined.
   * If nil, component should be used.
   */
  const elementId = params.elementId;
  const adminSettingsClaim = useSelector((state) => state.user.data?.Claims['sq.administrator.settings']);
  const adminPage = useSelector((state) => state.admin.adminPage);
  const showNotifications = useSelector((state) =>
    state.admin.licensedProducts?.some((p) => p === 'QESTField Scheduler')
  );
  const initialisingAdminData = useSelector((state) => state.admin.status.initialising);
  const initialisingPage = useSelector((state) => state.admin.status.initialisingPage);

  const syncingEnvironmentData = useSelector((state) => state.environment.status.syncing);
  const agsPermission = useSelector((state) => state.admin.agsLicensed);
  const dispatch = useDispatch();
  const links = getLinks(showNotifications, agsPermission);

  // Important that this uses what the state thinks is the page, not the URI, or you will see a flash of the new screen
  // before loading begins, because the loading state comes late from the state changes.
  const AdminSection = isNil(elementId)
    ? adminPageConfiguration[adminPage]?.component
    : adminPageConfiguration[adminPage]?.individualComponent;

  useEffect(() => {
    dispatch(actions.init());
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.initPage({ adminPage: component, elementId }));
    if (adminPageConfiguration[component]?.environmentDataRequired) dispatch(environmentActions.getEnvironmentData());
  }, [dispatch, component, elementId]);

  if (!adminSettingsClaim)
    return (
      <Page>
        <PageSection>
          <Message warning>You must be a system administrator to access these settings.</Message>
        </PageSection>
      </Page>
    );

  if (initialisingAdminData || syncingEnvironmentData)
    return (
      <Page>
        <div style={{ position: 'relative', height: '100vh' }}>
          <Spinner large />
        </div>
      </Page>
    );

  if (!AdminSection && adminPage)
    return (
      <Page navLinks={links} navHeading="Administration">
        <PageSection>
          <Message error>Admin page not found. Please select an item from the menu.</Message>
        </PageSection>
      </Page>
    );

  if (initialisingPage)
    return (
      <Page navLinks={links} navHeading="Administration">
        <div style={{ position: 'relative', height: '100vh' }}>
          <Spinner large />
        </div>
      </Page>
    );

  return (
    <>
      <Page navLinks={links} navHeading="Administration">
        {!AdminSection ? (
          <PageSection>
            <Message>Select an option.</Message>
          </PageSection>
        ) : (
          <ErrorBoundary>
            <AdminInjectedPropsProvider>
              <div className="AdminPage">
                <FixedAdminHeader pageIdentifier={adminPageConfiguration[adminPage]?.headerName} />
                <div className="AdminPage-Content">
                  <AdminSection />
                </div>
              </div>
            </AdminInjectedPropsProvider>
          </ErrorBoundary>
        )}
      </Page>
    </>
  );
};

export default AdminPage;
